import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TodayIcon from "@mui/icons-material/Today";
import UpdateIcon from "@mui/icons-material/Update";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useMemo, useState } from "react";
import { useMutation } from "react-query";
import { AnalyticTable } from "../../api/analyticTable";
import { CreateExportDto } from "../../dto/CreateExportDto";
import { useAuth } from "../../hooks/useAuth";
import { AnalyticExport } from "../../shared/types/analyticExport.type";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);

export type PreviousExport = {
  id: number;
  layout_name: string;
  status: 0 | 1 | 2 | 4;
  file_url?: string | null;
  created_at: string;
  data_range_start: string | null;
  data_range_end: string | null;
};

export type ExportInput = {
  date_start?: string;
  date_end?: string;
  format_for_export: boolean;
};

type PresetKey = "today" | "two_weeks" | "since_last" | "all";

type Props = {
  previousExports?: AnalyticExport[];
  onCancel?: () => void;
  onCreated?: () => void;
  viewName?: string;
  viewId?: string;
  isFetching?: boolean;
};

export const YMD = "YYYY-MM-DD";
const USER_TZ = dayjs.tz.guess();
export const DEFAULT_ERROR = "error:generic";

export const inUserTz = (iso?: string | Date) =>
  (iso ? dayjs(iso) : dayjs()).tz(USER_TZ).locale("fr");

const fmtLong = (d: Dayjs | string) =>
  dayjs(d).locale("fr").format("D MMM YYYY");

export const buildInput = (
  start?: string | null,
  end?: string | null,
): ExportInput => ({
  ...(start ? { date_start: start } : {}),
  ...(end ? { date_end: end } : {}),
  format_for_export: true,
});

export const apiMessage = (err: any): string | null =>
  err?.response?.data?.detail ||
  err?.response?.data?.["hydra:description"] ||
  err?.response?.data?.violations?.map((v: any) => v.message).join(" ") ||
  null;

/** Retry a previous export with the same date range. Use in the parent next to <ExportHistory />. */

function CreateExport({
  previousExports = [],
  onCancel,
  onCreated,
  viewName,
  viewId,
  isFetching
}: Props) {
  const { auth } = useAuth();
  const createExport = useMutation((data: CreateExportDto) =>
    AnalyticTable.createExportAnalytics(data),
  );

  const { t } = useTranslation();

  const lastSuccess = useMemo(
    () => previousExports.find((e) => e.status === 2),
    [previousExports],
  );

  const getPresetRange = (key: PresetKey) => {
    const today = inUserTz();
    const end = today.format(YMD);
    switch (key) {
      case "today":
        return { start: end, end };
      case "two_weeks":
        return { start: today.subtract(14, "day").format(YMD), end };
      case "since_last": {
        if (!lastSuccess) return { start: "", end: "" };
        let start = lastSuccess.data_range_end
          ? inUserTz(lastSuccess.data_range_end).add(1, "day").format(YMD)
          : inUserTz(lastSuccess.created_at).format(YMD);
        if (dayjs(start).isAfter(dayjs(end), "day")) start = end; // already exported up to today
        return { start, end };
      }
      default:
        return { start: "", end: "" };
    }
  };

  const [preset, setPreset] = useState<PresetKey | null>("today");
  const [dateStart, setDateStart] = useState(
    () => getPresetRange("today").start,
  );
  const [dateEnd, setDateEnd] = useState(() => getPresetRange("today").end);
  const [error, setError] = useState<string | null>(null);

  const tzLabel = useMemo(
    () => `${USER_TZ} (UTC${inUserTz().format("Z")})`,
    [],
  );

  const presets: {
    key: PresetKey;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    disabled?: boolean;
  }[] = [
    {
      key: "today",
      title: t("daterangepicker:today"),
      subtitle: t("export:dataOfToday"),
      icon: <TodayIcon fontSize="small" />,
    },
    {
      key: "two_weeks",
      title: t("daterangepicker:last2weeks"),
      subtitle: t("export:fourteenDays"),
      icon: <CalendarMonthIcon fontSize="small" />,
    },
    {
      key: "since_last",
      title: t("export:sinceLastSuccess"),
      subtitle: lastSuccess
        ? t("export:lastSuccess", {
            date: fmtLong(inUserTz(lastSuccess.created_at)),
          })
        : t("export:noPreviousExport"),
      icon: <UpdateIcon fontSize="small" />,
      disabled: !lastSuccess,
    },
    {
      key: "all",
      title: t("export:allHistory"),
      subtitle: t("export:allData"),
      icon: <AllInclusiveIcon fontSize="small" />,
    },
  ];

  const toPicker = (v: string): Dayjs | null => (v ? dayjs(v) : null);

  const selectPreset = (key: PresetKey) => {
    const r = getPresetRange(key);
    setPreset(key);
    setDateStart(r.start);
    setDateEnd(r.end);
    setError(null);
  };

  const onDateChange = (field: "start" | "end", v: Dayjs | null) => {
    const value = v && v.isValid() ? v.format(YMD) : "";
    if (field === "start") setDateStart(value);
    else setDateEnd(value);
    setPreset(null);
    setError(null);
  };

  const isAll = preset === "all";
  const exportsAll = !dateStart && !dateEnd;
  const invalidRange =
    !!dateStart && !!dateEnd && dayjs(dateStart).isAfter(dayjs(dateEnd), "day");

  const summary = exportsAll
    ? t("export:allData")
    : dateStart && dateEnd
      ? dateStart === dateEnd
        ? fmtLong(dateStart)
        : `${fmtLong(dateStart)} → ${fmtLong(dateEnd)}`
      : dateStart
        ?  `${t('training:qualificationItem:from')} ${fmtLong(dateStart)}`
        : `${t("daterangepicker:toLabel")} ${fmtLong(dateEnd)}`;

  const handleSubmit = async () => {
    if (invalidRange) {
      setError("La date de début doit être antérieure à la date de fin.");
      return;
    }
    setError(null);
    try {
      await createExport.mutateAsync({
        input: buildInput(dateStart, dateEnd),
        user: `${auth?.["@id"]}`,
        layout_name: viewName || "Default Layout",
        analytics_view: viewId ? `${viewId}` : undefined,
      } as CreateExportDto);
      onCreated?.();
    } catch (err) {
      setError(apiMessage(err) ?? DEFAULT_ERROR);
    }
  };

  const submitting = createExport.isLoading;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
          {t("export:periodToExport")}
        </Typography>
        <Box
          role="group"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 1.5,
          }}
        >
          {presets.map((p) => {
            const selected = preset === p.key;
            return (
              <ButtonBase
                key={p.key}
                disabled={p.disabled}
                aria-pressed={selected}
                onClick={() => selectPreset(p.key)}
                sx={(t) => ({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.25,
                  textAlign: "left",
                  p: 1.75,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  bgcolor: selected
                    ? alpha(t.palette.primary.main, 0.06)
                    : "background.paper",
                  boxShadow: selected
                    ? `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}`
                    : "none",
                  opacity: p.disabled ? 0.5 : 1,
                  transition: t.transitions.create(
                    ["border-color", "background-color", "box-shadow"],
                    { duration: 150 },
                  ),
                  "&:hover:not(.Mui-disabled)": {
                    borderColor: selected ? "primary.main" : "text.secondary",
                  },
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${t.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                })}
              >
                <Box
                  sx={(t) => ({
                    display: "grid",
                    placeItems: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    color: selected ? "primary.main" : "text.secondary",
                    bgcolor: selected
                      ? alpha(t.palette.primary.main, 0.12)
                      : t.palette.action.hover,
                  })}
                >
                  {p.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                  >
                    {p.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.subtitle}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {exportsAll && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
          {t("export:warning:allDataExport")}
        </Alert>
      )}

      <Box
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
      >
        <Box
          sx={{
            p: 2.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            opacity: isAll ? 0.5 : 1,
            transition: "opacity .15s",
          }}
        >
          {(["start", "end"] as const).map((field) => (
            <Box key={field}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, mb: 0.75, color: "text.secondary" }}
              >
                {field === "start"
                  ? t("riders:startDate")
                  : t("riders:endDate")}
              </Typography>
              <DatePicker
                value={toPicker(field === "start" ? dateStart : dateEnd)}
                onChange={(v) => onDateChange(field, v)}
                disabled={isAll}
                {...(field === "start"
                  ? { maxDate: toPicker(dateEnd) ?? undefined }
                  : { minDate: toPicker(dateStart) ?? undefined })}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: invalidRange,
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={(t) => ({
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: t.palette.action.hover,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          })}
        >
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <EventIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {summary}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("notification:field:timezone")}: {tzLabel}
          </Typography>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button color="inherit" onClick={onCancel} disabled={submitting}>
          {t("cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting || invalidRange}
          startIcon={<FileDownloadIcon />}
        >
          {t("export:buttons:startExport")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

export default CreateExport;
